// @ts-check

import {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  useSyncExternalStore,
} from 'react';
import { requestWithToken } from '../utilities/request';
import _ from 'lodash';

/**
 * @typedef returnUseFetchDataBase
 * @property {object} data requestWithTokenの結果 \
 * 通信が正常な場合のみ変化
 * @property {boolean} isLodaing 通信中か
 * @property {boolean} isInitLoded 初期読み込みが完了したか \
 * 通信が正常な場合のみ変化
 * @property {boolean} isError エラーが発生したか \
 * 再通信開始時にリセット
 * @property {any} errorInfo requestWithTokenのエラーオブジェクト \
 * エラー発生時のみ変化
 * @property {function} reloadAction リロードを発生させる関数 \
 * イベントハンドラ内で通信を行った結果、 \
 * フロントとサーバのデータに差異ができた際に \
 * 再読み込みする場合に使用する \
 * 取得した結果を(再描画前に)即座に用いて何かを実行したい場合は、 \
 * dataではなくreloadActionの戻り値を使用すること
 */

/**
 * トークン込みでデータを取得するカスタムフック \
 * 基本、初期データ取得に使用する \
 * ボタン押下時のデータ送信などは、 \
 * 各種イベントハンドラの中で直接requestWithTokenを呼び出すこと
 * @param {object} options requestWithTokenに引き渡すoption
 * @param {any} init_data 初期データ \
 * 必須 \
 * 初回render時はこのデータを取得するので、 \
 * エラーにならないようなオブジェクトを渡す
 * @param {boolean} useDefaultCatch requestWithTokenに引き渡すuseDefaultCatch
 * @param {boolean} showLoading requestWithTokenに引き渡すshowLoading
 * @param {boolean} isFetchStart 読み込みを実施して良いか \
 * falseだとreloadActionでも読み込みしないので注意
 * @return {returnUseFetchDataBase} returnUseFetchDataWithTokenの戻り値
 **/
function useFetchDataWithToken(
  options,
  init_data,
  useDefaultCatch = true,
  showLoading = true,
  isFetchStart = true
) {
  const prevOptions = useRef(options);

  // 1つ前の状態の値を保持しておき、深い比較をした結果中身が同じなら1つ前のオブジェクトを返す。
  // これによって、getDataが無駄に変わることを防ぐ(useCallbackは浅い比較なので)
  const optionsFromDeepCompareResult = useMemo(() => {
    if (_.isEqual(prevOptions.current, options)) {
      return prevOptions.current;
    } else {
      prevOptions.current = options;
      return options;
    }
  }, [options]);

  const getData = useCallback(async () => {
    const data = requestWithToken(
      optionsFromDeepCompareResult,
      useDefaultCatch,
      showLoading
    );
    return data;
  }, [optionsFromDeepCompareResult, useDefaultCatch, showLoading]);

  const {
    data,
    isLodaing,
    isInitLoded,
    isError,
    errorInfo,
    reloadAction
  } = useFetchDataBase(getData, init_data, isFetchStart);

  return { data, isLodaing, isInitLoded, isError, errorInfo, reloadAction };
}

/**
 * データを取得するカスタムフック
 * ベース用であり、これは直接公開しない
 * @param {function} getData データを取得するための関数 \
 * 必須 \
 * render毎の動作を防止するためuseCallbackで囲むこと
 * @param {any} init_data 初期データ \
 * 必須 \
 * 初回render時はこのデータを取得するので、エラーにならないようなオブジェクトを渡す
 * @param {boolean} isFetchStart 読み込みを実施して良いか \
 * falseだとreloadActionでも読み込みしないので注意
 * @return {returnUseFetchDataBase} returnUseFetchDataBaseの戻り値
 **/
function useFetchDataBase(getData, init_data, isFetchStart = true) {
  const cacheData = useRef(init_data);
  const cacheCallback = useRef(() => {});
  const [isInitLoded, setIsInitLoded] = useState(false);
  const [isLodaing, setIsLoding] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);
  const reloadActionIgnore = useRef(false);

  // NOTE：NodeJSが15.4.0以上になったらAbortControllerを併用して通信を止めるように修正すると良いはず
  //       参考：https://zenn.dev/mkt_engr/articles/abort-fetch-with-axios
  const fetchData = useCallback(
    (ignore, callback) => {
      // ingnoreについて：https://ja.react.dev/learn/synchronizing-with-effects#fetching-data
      const exec = async () => {
        if (isFetchStart) {
          setIsLoding(true);
          setIsError(false);
          setErrorInfo(null);
          try {
            const data = await getData();
            if (!ignore) {
              cacheData.current = data;
              setIsInitLoded(true);
              setIsLoding(false);
              callback();
              return data;
            }
          } catch (e) {
            if (!ignore) {
              setIsError(true);
              setErrorInfo(e);
              setIsLoding(false);
              return e;
            }
          }
        }
      };
      return exec();
    },
    [getData, isFetchStart]
  );

  const subscribe = useCallback(
    callback => {
      let ignore = false;
      cacheCallback.current = callback;
      fetchData(ignore, callback);
      return () => {
        ignore = true;
      };
    },
    [fetchData]
  );

  const getSnapshot = useCallback(() => {
    return cacheData.current;
  }, []);

  // useSyncExternalStoreを使っている理由は以下を参照
  // https://zenn.dev/kazuma1989/articles/a30ba6e29b5b4c
  const data = useSyncExternalStore(subscribe, getSnapshot);

  const reloadAction = useCallback(() => {
    return fetchData(reloadActionIgnore.current, cacheCallback.current);
  }, [fetchData]);


  // アンマウント時にリロードアクションの通信を止める用
  useEffect(() => {
    return () => {
      reloadActionIgnore.current = true;
    };
  }, []);

  return {
    data,
    isLodaing,
    isInitLoded,
    isError,
    errorInfo,
    reloadAction,
  };
}

export { useFetchDataWithToken };



// WEBPACK FOOTER //
// ./src/utilities/useFetchData.jsx